import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { ToastViewport } from '../ui/ToastViewport';
import {
  ToastContext,
  type ToastContextValue,
  type ToastInput,
  type ToastInstance,
} from './ToastContext';

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInstance[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string): void => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (input: ToastInput): string => {
      counter += 1;
      const id = `toast-${Date.now()}-${counter}`;
      const duration = input.duration ?? 5000;
      const instance: ToastInstance = { ...input, id };

      setToasts((current) => [...current, instance]);

      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, timer);
      }
      return id;
    },
    [dismiss]
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (message, options) => show({ tone: 'success', message, ...options }),
      error: (message, options) => show({ tone: 'error', message, ...options }),
      warning: (message, options) => show({ tone: 'warning', message, ...options }),
      info: (message, options) => show({ tone: 'info', message, ...options }),
      dismiss,
    }),
    [show, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}
